/**
 * Airtable Configuration Module
 * 
 * Manages API keys, base IDs, and configuration for Airtable APIs
 * Provides secure access to credentials and configuration settings
 */

import { getDynamicBaseSchema } from './metadata-api';

// Environment variable keys
const ENV = {
  AIRTABLE_PAT: 'NEXT_PUBLIC_AIRTABLE_PAT',
  AIRTABLE_BASE_ID: 'NEXT_PUBLIC_AIRTABLE_BASE_ID',
  NODE_ENV: 'NODE_ENV',
};

// Default values and constants
const DEFAULTS = {
  AIRTABLE_BASE_ID: 'appWjWMPvJE4l72uK',
  CACHE_TTL: 10 * 60, // 10 minutes in seconds
  CACHE_MAX_SIZE: 500, // Maximum number of items in cache
  CACHE_PRELOAD_TABLES: ['PROVIDERS'],
  CACHE_LRU_ENABLED: true, // Use LRU eviction policy
  CACHE_STATS_INTERVAL: 60, // Log stats every 60 seconds (0 to disable)
  CACHE_DEBUG: false, // Enable verbose debug logging
};

// Airtable API endpoints
export const ENDPOINTS = {
  REST_API: 'https://api.airtable.com/v0',
  METADATA_API: 'https://api.airtable.com/v0/meta',
};

/**
 * Get configuration value with fallback to default
 */
function getConfigValue(key: string, defaultValue?: string): string {
  // Only access secrets server-side
  if (typeof window !== 'undefined' && (key === 'AIRTABLE_PAT' || key === 'NEXT_PUBLIC_AIRTABLE_PAT')) {
    console.warn(`Attempted to access secret env var ${key} on the client. Returning empty string.`);
    return '';
  }
  const value = process.env[key];
  if (!value && defaultValue !== undefined) {
    return defaultValue;
  }
  if (!value) {
    console.warn(`Configuration key ${key} is not set`);
    return '';
  }
  return value;
}

/**
 * Airtable configuration object
 */
export const airtableConfig = {
  // API credentials
  pat: (typeof window === 'undefined') ? getConfigValue(ENV.AIRTABLE_PAT) : '',
  baseId: getConfigValue(ENV.AIRTABLE_BASE_ID, DEFAULTS.AIRTABLE_BASE_ID),
  
  // Cache settings
  cache: {
    ttl: DEFAULTS.CACHE_TTL,
    enabled: true,
    maxSize: DEFAULTS.CACHE_MAX_SIZE,
    lruEviction: DEFAULTS.CACHE_LRU_ENABLED,
    preloadTables: DEFAULTS.CACHE_PRELOAD_TABLES,
    statsInterval: DEFAULTS.CACHE_STATS_INTERVAL,
    debug: DEFAULTS.CACHE_DEBUG,
  },
  
  // Environment
  isDevelopment: process.env.NODE_ENV !== 'production',
  
  // Validation
  isConfigValid(): boolean {
    // Use the same credential logic that getAirtableCredentials uses
    const pat = process.env.AIRTABLE_PAT || process.env.NEXT_PUBLIC_AIRTABLE_PAT || '';
    const baseId = process.env.AIRTABLE_BASE_ID || process.env.NEXT_PUBLIC_AIRTABLE_BASE_ID || '';
    return Boolean(pat && baseId);
  },
  
  // Generate auth headers for REST API and Metadata API (always use PAT)
  getAuthHeaders(): HeadersInit {
    if (typeof window !== 'undefined') {
      console.warn('Attempted to access Airtable PAT on the client. Returning empty headers.');
      return { 'Content-Type': 'application/json' };
    }
    const pat = process.env.AIRTABLE_PAT || process.env.NEXT_PUBLIC_AIRTABLE_PAT || this.pat;
    return {
      Authorization: `Bearer ${pat}`,
      'Content-Type': 'application/json',
    };
  },
  getMetadataAuthHeaders(): HeadersInit {
    if (typeof window !== 'undefined') {
      console.warn('Attempted to access Airtable PAT on the client. Returning empty headers.');
      return { 'Content-Type': 'application/json' };
    }
    const pat = process.env.AIRTABLE_PAT || process.env.NEXT_PUBLIC_AIRTABLE_PAT || this.pat;
    return {
      Authorization: `Bearer ${pat}`,
      'Content-Type': 'application/json',
    };
  },
};

/**
 * Validate configuration during initialization
 */
export function validateAirtableConfig(): boolean {
  // Use getAirtableCredentials to check if configuration is valid
  const { personalAccessToken, baseId } = getAirtableCredentials();
  if (!personalAccessToken || !baseId) {
    // In browser environment, only show warning instead of error since server-side might still work
    if (typeof window !== 'undefined') {
      console.warn('Airtable client-side configuration is incomplete. API calls will be handled by server.');
    } else {
      console.error('Airtable configuration is invalid. Please check your environment variables.');
    }
    return false;
  }
  return true;
}

// Only validate on server-side or if window is defined (client-side)
if (typeof window === 'undefined' || typeof window !== 'undefined') {
  validateAirtableConfig();
}

/**
 * Airtable configuration and constants
 */

// API configuration
export const AIRTABLE_API = {
  BASE_URL: 'https://api.airtable.com/v0',
  VERSION: 'v0',
}

// Logical keys for tables (DEPRECATED: Only use for UI/debug, not for logic)
/**
 * @deprecated Use table IDs from AIRTABLE_TABLES for all logic. Logical keys are for UI/debug only.
 */
export const LOGICAL_TABLE_KEYS = [
  'PROVIDERS',
  'PRODUCTS',
  'SERVICES',
  'CROS',
  'TECHNOLOGY',
];

/**
 * @deprecated Use table IDs from AIRTABLE_TABLES for all logic. Logical keys are for UI/debug only.
 */
export const CACHE_PRELOAD_TABLES = [
  'PROVIDERS',
  'PRODUCTS',
  'SERVICES',
  'CROS',
  'TECHNOLOGY',
];

/**
 * Reference mapping for table ID → display name (logic must use table IDs only)
 */
export const AIRTABLE_TABLES: Record<string, { id: string; name: string }> = {
  PROVIDERS: { id: 'tbl44pQiTqeuuHOtM', name: 'Providers' },
  PRODUCTS: { id: 'tblvkFfJo6IbHxeaz', name: 'Products' },
  PROVIDER_SERVICES: { id: 'tblPdLGnifTXszCgM', name: 'Provider Services' },
  CRO_SERVICES: { id: 'tblVQWNsC1n5oPUo4', name: 'CRO Services' },
  CROS: { id: 'tblZSKT3d4G3YgWHb', name: 'CROs' },
  TECHNOLOGY: { id: 'tblU4vOs5XVeDvwI4', name: 'Technology' },
  PLATFORMS: { id: 'tblD6Knh1X6fq68Ea', name: 'Platforms' },
};

/**
 * Get Airtable table ID by logical key (DEPRECATED: Use table IDs directly for all logic)
 * @deprecated Use table IDs directly for all logic. This is for legacy support only.
 */
export async function getTableIdByKey(key: string): Promise<string> {
  console.warn('[DEPRECATED] getTableIdByKey is deprecated. Use table IDs directly for all logic.');
  // Fallback to AIRTABLE_TABLES mapping for UI/debug only
  return AIRTABLE_TABLES[key]?.id || key;
}

/**
 * Get Airtable table meta (ID and name) by logical key (DEPRECATED: Use table IDs directly for all logic)
 * @deprecated Use table IDs directly for all logic. This is for UI/debug only.
 */
export async function getTableMetaByKey(key: string): Promise<{ id: string; name: string }> {
  console.warn('[DEPRECATED] getTableMetaByKey is deprecated. Use table IDs directly for all logic.');
  return AIRTABLE_TABLES[key] || { id: key, name: key };
}

/**
 * DEPRECATED: Get table name by logical key (do not use for any logic)
 * This is a stub for backward compatibility only.
 */
export async function getTableNameByKey(key: string): Promise<string> {
  console.warn('[DEPRECATED] getTableNameByKey is deprecated. Use table IDs directly for all logic and API calls.');
  return AIRTABLE_TABLES[key]?.name || key;
}

/**
 * Get Airtable API credentials from environment variables
 */
export function getAirtableCredentials() {
  return {
    personalAccessToken: process.env.AIRTABLE_PAT || process.env.NEXT_PUBLIC_AIRTABLE_PAT || '',
    baseId: process.env.AIRTABLE_BASE_ID || process.env.NEXT_PUBLIC_AIRTABLE_BASE_ID || '',
  };
}

/**
 * Build Airtable API URL for a specific table
 */
export async function buildAirtableUrl(baseId: string, tableKey: string = 'SPATIAL_BIOLOGY_PLATFORMS', recordIds?: string[]): Promise<string> {
  const tableName = await getTableNameByKey(tableKey);
  let url = `${AIRTABLE_API.BASE_URL}/${baseId}/${encodeURIComponent(tableName)}`;
  if (recordIds && recordIds.length > 0) {
    // Build filterByFormula for record IDs
    const formula = 'OR(' + recordIds.map(id => `RECORD_ID()='${id}'`).join(',') + ')';
    url += `?filterByFormula=${encodeURIComponent(formula)}`;
  }
  return url;
}

// Optionally, provide an async function to initialize config if needed
// ... existing code ... 

export const DEFAULT_TABLE = DEFAULTS.CACHE_PRELOAD_TABLES[0];
